import { createRoot } from 'react-dom/client'
import { StrictMode } from 'react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

// Styles
import './styles/main.scss'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Import configured query client
import { queryClient } from '@/lib/query-client'

// Import AuthProvider and hook
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { ImpersonationProvider } from '@/contexts/ImpersonationContext'
import type { User } from '@/types/supabase'

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    user: undefined as User | null | undefined,
    authLoading: true,
  },
  defaultPreload: 'intent', // Prefetch on hover/focus
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Wrapper component to sync auth state with router context
function InnerApp() {
  const { user, loading } = useAuth()

  // Update router context when user changes
  router.update({
    context: {
      queryClient,
      user,
      authLoading: loading,
    },
  })

  // Don't render the router until auth is ready
  // This prevents route guards from running with undefined auth state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e5e5e5',
          borderTopColor: '#0f766e',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  return (
    <>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ImpersonationProvider>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </ImpersonationProvider>
    </QueryClientProvider>
  </StrictMode>,
)
